import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Edit2, Trash2, Plus, ChevronDown, ChevronRight, RefreshCw, Sliders } from 'react-feather';
import menuApi from 'api/requests/menu';
import BottomSheet from 'components/_UI/BottomSheet';
import OptionGroupForm from '../forms/OptionGroupForm';
import ConfirmDialog from '../components/ConfirmDialog';
import colors from 'theme/colors';

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default forwardRef(function OptionGroupsTab({ slug, toast }, ref) {
    const [groups, setGroups] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sheet, setSheet] = useState({ open: false, data: null });
    const [confirm, setConfirm] = useState({ open: false, type: null, groupId: null, optionId: null, name: '' });
    const [deleting, setDeleting] = useState(false);

    useImperativeHandle(ref, () => ({
        openCreate() { setSheet({ open: true, data: null }); }
    }));

    useEffect(() => { fetchGroups(); }, []);

    async function fetchGroups() {
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await menuApi.getOptionGroups(slug);
            setGroups(res.data || []);
        } catch {
            setError('Não foi possível carregar os grupos de opções.');
        } finally {
            setLoading(false);
        }
    }

    function toggleExpand(id) {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    }

    function requestDeleteGroup(group) {
        setConfirm({ open: true, type: 'group', groupId: group.id, optionId: null, name: group.name });
    }

    function requestDeleteOption(groupId, option) {
        setConfirm({ open: true, type: 'option', groupId, optionId: option.id, name: option.name });
    }

    async function handleConfirmDelete() {
        setDeleting(true);
        try {
            if (confirm.type === 'group') {
                await menuApi.deleteOptionGroup(slug, confirm.groupId);
                setGroups(prev => prev.filter(g => g.id !== confirm.groupId));
                toast?.success('Grupo removido com sucesso');
            } else {
                await menuApi.deleteOption(confirm.optionId);
                setGroups(prev => prev.map(g =>
                    g.id === confirm.groupId
                        ? { ...g, options: g.options.filter(o => o.id !== confirm.optionId) }
                        : g
                ));
                toast?.success('Opção removida com sucesso');
            }
        } catch {
            toast?.error(confirm.type === 'group' ? 'Erro ao remover grupo' : 'Erro ao remover opção');
        } finally {
            setDeleting(false);
            setConfirm({ open: false, type: null, groupId: null, optionId: null, name: '' });
        }
    }

    function handleSaved(saved) {
        const isEdit = groups.find(g => g.id === saved.id);
        setGroups(prev => {
            const exists = prev.find(g => g.id === saved.id);
            return exists ? prev.map(g => g.id === saved.id ? saved : g) : [...prev, saved];
        });
        toast?.success(isEdit ? 'Grupo atualizado' : 'Grupo criado com sucesso');
        setSheet({ open: false, data: null });
    }

    if (loading) return <LoadingContainer><Spinner /><LoadingText>Carregando grupos...</LoadingText></LoadingContainer>;

    if (error) {
        return (
            <ErrorState>
                <ErrorIcon><RefreshCw size={24} color={colors.danger} /></ErrorIcon>
                <ErrorTitle>{error}</ErrorTitle>
                <RetryBtn onClick={fetchGroups} aria-label='Tentar novamente'>
                    <RefreshCw size={14} /> Tentar novamente
                </RetryBtn>
            </ErrorState>
        );
    }

    return (
        <Container>
            {groups.length === 0 && (
                <EmptyState>
                    <EmptyIconWrapper><Sliders size={32} color={colors.occasionalPurple} /></EmptyIconWrapper>
                    <EmptyText>Nenhum grupo de opções criado</EmptyText>
                    <EmptyHint>Grupos de opções são reutilizáveis entre produtos (ex: Ponto da Carne, Adicionais)</EmptyHint>
                    <EmptyCTA onClick={() => setSheet({ open: true, data: null })}>
                        <Plus size={16} /> Criar primeiro grupo
                    </EmptyCTA>
                </EmptyState>
            )}

            {groups.map(group => (
                <GroupCard key={group.id}>
                    <GroupHeader
                        onClick={() => toggleExpand(group.id)}
                        aria-expanded={!!expanded[group.id]}
                        aria-controls={`group-options-${group.id}`}
                    >
                        <GroupInfo>
                            <GroupName>{group.name}</GroupName>
                            <GroupMeta>
                                <MetaBadge $type={group.is_required ? 'required' : 'optional'}>
                                    {group.is_required ? 'Obrigatório' : 'Opcional'}
                                </MetaBadge>
                                <MetaText>{group.options?.length ?? 0} opç{group.options?.length !== 1 ? 'ões' : 'ão'}</MetaText>
                                {group.min_selections != null && group.max_selections != null && (
                                    <MetaText>({group.min_selections}-{group.max_selections} seleções)</MetaText>
                                )}
                            </GroupMeta>
                        </GroupInfo>
                        <GroupActions>
                            <ActionBtn
                                onClick={e => { e.stopPropagation(); setSheet({ open: true, data: group }); }}
                                aria-label={`Editar grupo ${group.name}`}
                            >
                                <Edit2 size={15} color={colors.night} />
                            </ActionBtn>
                            <ActionBtn
                                onClick={e => { e.stopPropagation(); requestDeleteGroup(group); }}
                                aria-label={`Remover grupo ${group.name}`}
                            >
                                <Trash2 size={15} color={colors.danger} />
                            </ActionBtn>
                            <ChevronBtn aria-hidden='true'>
                                {expanded[group.id]
                                    ? <ChevronDown size={16} color='#999' />
                                    : <ChevronRight size={16} color='#999' />
                                }
                            </ChevronBtn>
                        </GroupActions>
                    </GroupHeader>

                    {expanded[group.id] && (
                        <OptionsList id={`group-options-${group.id}`}>
                            {group.options?.length === 0 && (
                                <NoOptions>Nenhuma opção. Edite o grupo para adicionar.</NoOptions>
                            )}
                            {group.options?.map(opt => (
                                <OptionRow key={opt.id}>
                                    <OptionName>{opt.name}</OptionName>
                                    <OptionRight>
                                        {Number(opt.price_adjustment) > 0 && (
                                            <OptionPrice>+{formatPrice(opt.price_adjustment)}</OptionPrice>
                                        )}
                                        <ActionBtn
                                            onClick={() => requestDeleteOption(group.id, opt)}
                                            aria-label={`Remover opção ${opt.name}`}
                                        >
                                            <Trash2 size={13} color={colors.danger} />
                                        </ActionBtn>
                                    </OptionRight>
                                </OptionRow>
                            ))}
                        </OptionsList>
                    )}
                </GroupCard>
            ))}

            <BottomSheet
                isOpen={sheet.open}
                onClose={() => setSheet({ open: false, data: null })}
                title={sheet.data ? 'Editar grupo' : 'Novo grupo de opções'}
                size='large'
            >
                <OptionGroupForm
                    slug={slug}
                    initialData={sheet.data}
                    onSaved={handleSaved}
                    onCancel={() => setSheet({ open: false, data: null })}
                />
            </BottomSheet>

            <ConfirmDialog
                isOpen={confirm.open}
                title={confirm.type === 'group' ? 'Remover grupo' : 'Remover opção'}
                message={
                    confirm.type === 'group'
                        ? `Tem certeza que deseja remover "${confirm.name}" e todas as suas opções? Esta ação não pode ser desfeita.`
                        : `Tem certeza que deseja remover a opção "${confirm.name}"?`
                }
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirm({ open: false, type: null, groupId: null, optionId: null, name: '' })}
            />
        </Container>
    );
});

/* --- Styled Components --- */

const Container = styled.div`
    padding: 20px 16px 24px;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const GroupCard = styled.div`
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    transition: box-shadow 0.15s;
    &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.1); }
`;

const GroupHeader = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    &:hover { background: ${colors.neutralDark}20; }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: -2px; }
`;

const GroupInfo = styled.div`display: flex; flex-direction: column; gap: 5px;`;
const GroupName = styled.span`font-weight: 700; font-size: 15px; color: ${colors.nightDark};`;
const GroupMeta = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;

const MetaBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    background: ${({ $type }) => $type === 'required' ? `${colors.danger}18` : `${colors.nightLight}20`};
    color: ${({ $type }) => $type === 'required' ? colors.danger : '#666'};
`;

const MetaText = styled.span`font-size: 12px; color: #666;`;

const GroupActions = styled.div`display: flex; align-items: center; gap: 4px;`;

const ActionBtn = styled.button`
    background: none; border: none; padding: 6px; cursor: pointer;
    border-radius: 6px; display: flex; align-items: center;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`;

const ChevronBtn = styled.div`padding: 4px; display: flex; align-items: center; margin-left: 4px;`;

const OptionsList = styled.div`
    padding: 4px 16px 12px;
    border-top: 1px solid rgba(0,0,0,0.06);
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid ${colors.neutralDark};
    &:last-child { border-bottom: none; }
`;

const OptionName = styled.span`font-size: 14px; color: ${colors.nightDark};`;
const OptionRight = styled.div`display: flex; align-items: center; gap: 8px;`;
const OptionPrice = styled.span`font-size: 13px; font-weight: 600; color: ${colors.occasionalPurple};`;
const NoOptions = styled.p`font-size: 13px; color: #666; text-align: center; padding: 12px 0; margin: 0;`;

const LoadingContainer = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px;
`;

const LoadingText = styled.span`font-size: 13px; color: #666;`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center;
`;

const EmptyIconWrapper = styled.div`
    width: 56px; height: 56px; border-radius: 50%; background: ${colors.occasionalPurple}10;
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
`;

const EmptyText = styled.p`font-weight: 700; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0 0 18px; max-width: 300px;`;

const EmptyCTA = styled.button`
    display: flex; align-items: center; gap: 8px; padding: 12px 24px; border-radius: 10px;
    background: ${colors.occasionalPurple}; color: #fff; font-size: 14px; font-weight: 600;
    border: none; cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const ErrorState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; text-align: center; gap: 8px;
`;

const ErrorIcon = styled.div`
    width: 48px; height: 48px; border-radius: 50%; background: ${colors.danger}10;
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
`;

const ErrorTitle = styled.p`font-weight: 600; font-size: 15px; color: ${colors.nightDark}; margin: 0;`;

const RetryBtn = styled.button`
    display: flex; align-items: center; gap: 6px; padding: 10px 20px; border-radius: 8px;
    background: none; border: 1.5px solid ${colors.neutralDark}; color: ${colors.nightDark};
    font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 8px;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;
